// components/portal/SourceBulkEnrichPanel.tsx
'use client';

import React, { useCallback, useMemo, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { useShallow } from 'zustand/react/shallow';
import { Sparkles, Loader2, RefreshCw, X } from '@/components/ui/lucide-memo';
import { toast } from 'sonner';
import { ACTIVE_SOURCING_CAMPAIGN_STATUSES } from '#lib/leasing/sourcingCampaignLifecycle';
import { PANEL_BUTTON, PANEL_BUTTON_FONT_SIZE } from './panelButton';
import { PanelSection } from './panelSection';
import { readCampaignRow } from '#lib/leasing/campaignRow';

interface SourceBulkEnrichPanelProps {
  /** Fit rows to enrich: the checkbox selection ('selection'), or the whole shortlist ('shortlist'). */
  fitIds: string[];
  lang: string;
  /**
   * 'selection' (default): the header reads "N brands selected" with a clear-selection button.
   * 'shortlist': the right bar of an OPEN campaign with nothing selected, the header reads
   * "N brands in this shortlist" and enriches the whole shortlist (no clear-selection button).
   */
  mode?: 'selection' | 'shortlist';
  /** Rendered inside another panel (the shortlist inspector): drop the outer scroll container
   *  and the summary header, which the host already provides. */
  embedded?: boolean;
}

// A shortlist in one of these states is already working, so it cannot take a re-run: the pass in
// progress would race it over the same leads. The list itself is NOT restated here: it is the one
// rule that says a campaign is working, it lives in lib/leasing/sourcingCampaignLifecycle.ts, and
// the route's guard reads it too. A local copy would keep offering a billed re-run the day a new
// active status is added there.
const isActiveCampaignStatus = (status: string) =>
  (ACTIVE_SOURCING_CAMPAIGN_STATUSES as readonly string[]).includes(status);

/**
 * Right-bar panel shown when one or more fit rows are selected (and none is expanded).
 * Lets the user enrich every selected brand in one go; the canvas progress bar runs
 * throughout (each enrichBrand bumps the shared sourceEnrichInFlight counter).
 */
// The key exists only to make the selector's result shallow-comparable. Split on the FIRST
// occurrence: ids are cuids and never contain this character, so the id half is unambiguous even
// when a brand name does contain it.
//
// Deliberately a PRINTABLE character. The first version used a literal NUL, which worked and even
// passed the whole gate, but it made the file binary to git (diffs render as "Bin 16057 -> 17742")
// and invisible to grep. A source file that tooling cannot read is not worth one byte of safety.
const BRAND_KEY_SEPARATOR = '|';

export const SourceBulkEnrichPanel = ({ fitIds, lang, mode = 'selection', embedded = false }: SourceBulkEnrichPanelProps) => {
  const isShortlist = mode === 'shortlist';
  const { kernel } = useKernel();
  const [isEnriching, setIsEnriching] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });

  // Resolve the unique brands behind the selected fits, INSIDE the selector.
  //
  // This used to subscribe to three whole registries (PortfolioFit, Lead, UnitFit) and derive from
  // them in a useMemo. Zustand compares by reference and every registry write produces a new
  // container, so a single row landing anywhere in any of the three re-rendered this panel and
  // recomputed the list. That is not rare here: this panel is open precisely while a campaign is
  // producing those rows, at the socket's ~300ms throttle.
  //
  // The result is a flat string array so `useShallow` can actually do its job. Shallow-comparing an
  // array of freshly-built `{brandId, name}` objects compares element REFERENCES, which differ on
  // every run, so it would never bail out and the fix would be decorative. Strings compare by value.
  const brandKeys = useKernelStore(
    useShallow((state) => {
      const portfolioFitRegistry = state.inventory.PortfolioFit.registry;
      const leadRegistry = state.inventory.Lead.registry;
      const seen = new Set<string>();
      const out: string[] = [];
      for (const fitId of fitIds) {
        // The selection can come from a portfolio, asset (Lead) or unit (UnitFit) campaign.
        const fit = (portfolioFitRegistry[fitId] || leadRegistry[fitId]) as
          { brandId?: string; brandName?: string | null; leadId?: string; leadBrandName?: string | null } | undefined;
        let brandId = fit?.brandId;
        let name: string | null | undefined = fit?.brandName;
        // UnitFit rows carry leadId (not brandId), resolve the brand via the Lead registry.
        if (!brandId && fit?.leadId) {
          const lead = leadRegistry[fit.leadId] as { brandId?: string; brandName?: string | null } | undefined;
          brandId = lead?.brandId;
          name = name || lead?.brandName || fit?.leadBrandName;
        }
        if (brandId && !seen.has(brandId)) {
          seen.add(brandId);
          out.push(`${brandId}${BRAND_KEY_SEPARATOR}${name || brandId}`);
        }
      }
      return out;
    })
  );

  const brands = useMemo(
    () =>
      brandKeys.map((key) => {
        const separator = key.indexOf(BRAND_KEY_SEPARATOR);
        return { brandId: key.slice(0, separator), name: key.slice(separator + 1) };
      }),
    [brandKeys]
  );

  const count = brands.length;
  // A previously-paused bulk enrich (via the composer's Stop button) waiting to resume.
  const pausedBrandIds = useKernelStore((state) => state.sourceEnrichPausedBrandIds);

  // Re-running the analysis belongs to the ASSET shortlist only, exactly as it does for a single
  // brand (SourceBrandPanel): there the selected rows ARE Leads, and a Lead names the
  // SourcingCampaign that produced it, which is both the run to re-enter and the thing that says
  // whether a pass is already going. Portfolio/unit rows come from other campaign kinds and have no
  // re-run, so the action simply does not appear for them.
  // Same treatment as `brandKeys` above, and for the same reason: derived in the selector, returned
  // as a flat string array so shallow comparison is by value. First element is the campaign id,
  // the rest are brand ids; an empty array means "no single run to re-enter".
  const rerunKeys = useKernelStore(
    useShallow((state) => {
      const leadRegistry = state.inventory.Lead.registry;
      const campaignIds = new Set<string>();
      const brandIds: string[] = [];
      const seen = new Set<string>();
      for (const fitId of fitIds) {
        const lead = leadRegistry[fitId] as { brandId?: string; lastCampaignId?: string | null } | undefined;
        const campaignId = typeof lead?.lastCampaignId === 'string' ? lead.lastCampaignId.trim() : '';
        const brandId = typeof lead?.brandId === 'string' ? lead.brandId.trim() : '';
        if (!campaignId || !brandId) continue;
        campaignIds.add(campaignId);
        if (seen.has(brandId)) continue;
        seen.add(brandId);
        brandIds.push(brandId);
      }
      // Every selected row has to belong to the SAME run: the pass re-enters one campaign row, and
      // a selection spanning two shortlists has no single row to re-enter. Offer nothing rather
      // than silently re-running half of it.
      if (campaignIds.size !== 1) return [];
      return [Array.from(campaignIds)[0]!, ...brandIds];
    })
  );

  const rerunTarget = useMemo(
    () => ({ campaignId: rerunKeys[0] ?? '', brandIds: rerunKeys.slice(1) }),
    [rerunKeys]
  );

  const rerunCampaignStatus = useKernelStore((state) =>
    rerunTarget.campaignId
      ? String(readCampaignRow(state.inventory, rerunTarget.campaignId)?.status ?? '').toUpperCase()
      : '',
  );
  const rerunCount = rerunTarget.brandIds.length;
  const rerunRunning = isActiveCampaignStatus(rerunCampaignStatus);
  const [isQueueingRerun, setIsQueueingRerun] = useState(false);

  /**
   * A BILLED pass: one credit per selected brand. It is therefore one deliberate click and nothing
   * else, refused while the shortlist is already working, and swallowed while this request is in
   * flight, so a double click cannot queue it twice.
   */
  const handleRerunSelection = useCallback(async () => {
    const k = kernel.current;
    if (!k || rerunCount === 0 || !rerunTarget.campaignId || rerunRunning || isQueueingRerun) return;
    setIsQueueingRerun(true);
    try {
      await k.rerunSourcingCampaignBrand(rerunTarget.campaignId, rerunTarget.brandIds);
      toast.success(
        lang === 'fr'
          ? `Nouvelle analyse lancée pour ${rerunCount} marque${rerunCount > 1 ? 's' : ''}.`
          : `New analysis started for ${rerunCount} brand${rerunCount > 1 ? 's' : ''}.`,
      );
    } catch (error: any) {
      console.error('[SourceBulkEnrichPanel] rerun analysis failed', error);
      toast.error(
        error?.message || (lang === 'fr' ? "Échec du lancement de l'analyse." : 'Failed to start the analysis.'),
      );
    } finally {
      setIsQueueingRerun(false);
    }
  }, [kernel, rerunTarget, rerunCount, rerunRunning, isQueueingRerun, lang]);

  // Purely visual shell around the Kernel run: the spinner and the progress bar. The run itself
  // (pool, pause, chat documentation, toasts) lives in kernel.bulkEnrichBrands.
  const runEnrichQueue = useCallback(async (queueBrandIds: string[]) => {
    if (!kernel.current || isEnriching || queueBrandIds.length === 0) return;
    setIsEnriching(true);
    setProgress({ done: 0, total: queueBrandIds.length });
    try {
      await kernel.current.bulkEnrichBrands(queueBrandIds, {
        lang,
        onProgress: (done, total) => setProgress({ done, total }),
      });
    } finally {
      setIsEnriching(false);
    }
  }, [kernel, isEnriching, lang]);

  const handleEnrichAll = useCallback(() => {
    void runEnrichQueue(brands.map((b) => b.brandId));
  }, [runEnrichQueue, brands]);

  const handleResumeEnrich = useCallback(() => {
    if (!pausedBrandIds?.length) return;
    void runEnrichQueue(pausedBrandIds);
  }, [runEnrichQueue, pausedBrandIds]);

  const clearSelection = useCallback(() => {
    kernel.current?.setState({ sourceSelectedFitIds: [] });
  }, [kernel]);

  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div className={embedded ? 'flex w-full flex-col' : 'custom-scrollbar flex h-full w-full flex-col overflow-y-auto px-3 py-3'}>
      {/* Selection / shortlist summary */}
      {embedded ? null : (
      <div className="flex items-center justify-between gap-2 rounded-[10px] p-2.5" style={{ backgroundColor: '#262626' }}>
        <div className="min-w-0">
          <div className="text-sm font-medium text-white">
            {count}{' '}
            {isShortlist
              ? (lang === 'fr' ? (count > 1 ? 'marques dans la shortlist' : 'marque dans la shortlist') : count > 1 ? 'brands in this shortlist' : 'brand in this shortlist')
              : (lang === 'fr' ? (count > 1 ? 'marques sélectionnées' : 'marque sélectionnée') : count > 1 ? 'brands selected' : 'brand selected')}
          </div>
          <div className="mt-0.5 text-[11px] text-[#6E6E6E]">
            {isShortlist
              ? (lang === 'fr' ? 'Enrichir la shortlist, sélectionnez des lignes pour cibler.' : 'Enrich the shortlist, select rows to narrow it.')
              : (lang === 'fr' ? 'Enrichir la sélection' : 'Bulk enrich the selection')}
          </div>
        </div>
        {isShortlist ? null : (
          <button
            type="button"
            onClick={clearSelection}
            disabled={isEnriching}
            title={lang === 'fr' ? 'Effacer la sélection' : 'Clear selection'}
            className="shrink-0 rounded-[6px] p-1 text-[#6E6E6E] transition-colors hover:bg-[#1A1A1A] hover:text-[#E8E8E8] disabled:opacity-40"
          >
            <X size={14} />
          </button>
        )}
      </div>
      )}

      {/* Enrich data. Same section as the selected brand's own enrich list, so the two right bars
          are one design. */}
      <div className={embedded ? 'mt-3' : 'mt-4'}>
        <PanelSection title={lang === 'fr' ? 'Enrichir les données' : 'Enrich data'}>
          {isEnriching ? (
            <div className="mt-2">
              <div className="mb-1 flex items-center justify-between text-[#B5B5B5]">
                <span style={{ fontSize: 11 }}>{lang === 'fr' ? 'Enrichissement…' : 'Enriching…'}</span>
                <span style={{ fontSize: 11 }}>
                  {progress.done}/{progress.total}
                </span>
              </div>
              <div className="h-[6px] w-full overflow-hidden rounded-full bg-[#2A2A2A]">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #0C2A82, #1E50C8, #8AAAEE)' }}
                />
              </div>
            </div>
          ) : null}

          <button
            type="button"
            onClick={handleEnrichAll}
            disabled={count === 0}
            className={`mt-3 ${PANEL_BUTTON}`}
            style={{ fontSize: PANEL_BUTTON_FONT_SIZE }}
          >
            {isEnriching ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                {lang === 'fr' ? 'Enrichissement…' : 'Enriching…'}
              </>
            ) : (
              <>
                <Sparkles size={14} />
                {isShortlist
                  ? (lang === 'fr' ? `Tout enrichir (${count})` : `Enrich all (${count})`)
                  : (lang === 'fr' ? `Enrichir la sélection (${count})` : `Enrich all selected (${count})`)}
              </>
            )}
          </button>

          {!isEnriching && (pausedBrandIds?.length ?? 0) > 0 ? (
            <button
              type="button"
              onClick={handleResumeEnrich}
              className={`mt-2 ${PANEL_BUTTON}`}
              style={{ fontSize: PANEL_BUTTON_FONT_SIZE }}
            >
              <Sparkles size={14} />
              {lang === 'fr'
                ? `Reprendre l'enrichissement (${pausedBrandIds?.length})`
                : `Resume enrichment (${pausedBrandIds?.length})`}
            </button>
          ) : null}
        </PanelSection>
      </div>

      {/* Score the selected brands again from scratch: fresh research, fresh judging, the same
          engine the run used, scoped to those rows alone. Same section idiom and same button skin
          as the selected brand's own "Relancer l'analyse", because it is the same action on more
          than one row. Only offered on an asset shortlist (see rerunTarget), and never on the whole
          shortlist: this bills one credit per brand. */}
      {!isShortlist && rerunCount > 0 ? (
        <div className="mt-3">
          <PanelSection title={lang === 'fr' ? 'Notation' : 'Scoring'}>
            <button
              type="button"
              onClick={handleRerunSelection}
              disabled={rerunRunning || isQueueingRerun || isEnriching}
              className={`mt-3 ${PANEL_BUTTON}`}
              style={{ fontSize: PANEL_BUTTON_FONT_SIZE }}
            >
              {rerunRunning || isQueueingRerun ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  {lang === 'fr' ? 'Analyse en cours…' : 'Analysis running…'}
                </>
              ) : (
                <>
                  <RefreshCw size={14} />
                  {lang === 'fr' ? `Relancer l'analyse (${rerunCount})` : `Run analysis again (${rerunCount})`}
                </>
              )}
            </button>
          </PanelSection>
        </div>
      ) : null}
    </div>
  );
};

SourceBulkEnrichPanel.displayName = 'SourceBulkEnrichPanel';
